import React, { forwardRef } from 'react';
import type { InputLabelProps as MuiInputLabelProps } from '@mui/material';
import { InputLabel as MuiInputLabel } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

/**
 This Component is a wrapper on MUI component.
 This component should be imported and not MUI directly!

 Explicit props based on actual usage in the codebase.
**/
// Deliberately does NOT extend `MuiInputLabelProps` (per .cursor/rules/fe-atomic-wrapper.mdc):
// extending type-permitted every MUI prop while the component forwards only the ones below and
// throws on the rest, so the two disagreed -- `InputLabel.test.tsx` already asserts that
// `<InputLabel focused>` throws. Extending the v5 props also widened `color` to include
// `error`/`info`/`success`/`warning`, which broke assignment where `Partial<InputLabelProps>`
// is reused to type the `InputLabelProps` object handed to the still-v4 TextField and pickers.
export interface InputLabelProps {
  /** The content of the label */
  children?: React.ReactNode;
  /** The id of the label element */
  id?: string;
  /** The id of the form element the label is bound to (rendered as the `for` attribute) */
  htmlFor?: string;
  /** CSS class name to apply to the root element */
  className?: string;
  /** Inline styles to apply to the root element */
  style?: React.CSSProperties;
  /** If true, the label is displayed in a disabled state */
  disabled?: boolean;
  /** If true, the label is pinned in its shrunk (floating) position */
  shrink?: MuiInputLabelProps['shrink'];
}

const InputLabel = forwardRef<HTMLLabelElement, InputLabelProps>(
  ({ children, id, htmlFor, className, style, disabled, shrink, ...restProps }, ref) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to InputLabel: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the InputLabelProps interface and to the component's signature.`,
        true
      );
    }
    return (
      <MuiInputLabel
        ref={ref}
        id={id}
        htmlFor={htmlFor}
        className={className}
        style={style}
        disabled={disabled}
        shrink={shrink}
      >
        {children}
      </MuiInputLabel>
    );
  }
);

InputLabel.displayName = 'InputLabel';

export default InputLabel;
